/* Anonyme, einwilligungsbasierte Nutzungsstatistik (Bimmer-Garage)
 *
 * GRUNDSATZ: Ohne ausdrueckliche Zustimmung passiert NICHTS. Kein Ereignis, keine Kennung, kein Speicher.
 *  - Die Einwilligung kommt aus Shopifys Customer Privacy API (dort schreibt das Cookie-Tool die Entscheidung hin):
 *    nur wenn analyticsProcessingAllowed() ausdruecklich true liefert, wird gesendet.
 *  - Die Einwilligung wird vor JEDEM einzelnen Senden erneut geprueft. Wird sie widerrufen, stoppt alles sofort,
 *    und die Session-Kennung wird geloescht.
 *  - "Do Not Track" bzw. "Global Privacy Control" im Browser gelten als Ablehnung.
 *  - Ist die API nicht verfuegbar, wird nicht getrackt (Fail-closed).
 *
 * Erfasst wird nichts Personenbezogenes: keine Namen, E-Mail-Adressen, keine IP-Adresse (wird von uns nicht gespeichert),
 * kein Spitzname oder Foto aus der Garage. Die Session-Kennung ist eine Zufallszahl im sessionStorage und
 * verschwindet beim Schliessen des Tabs.
 *
 * Konfiguration: snippets/bg-track-config.liquid ("enabled": false = komplett inaktiv).
 */
(() => {
  if (window.BGTrack) return;

  const TYPES = ['session_start', 'search', 'garage', 'product_view', 'add_to_cart', 'cart_view', 'checkout_click', 'survey', 'listing'];
  const SID_KEY = 'bg-sid';
  const STARTED_KEY = 'bg-sid-started';
  const SURVEY_KEY = 'bg-survey';

  const readJson = (id) => {
    try {
      const el = document.getElementById(id);
      return el ? JSON.parse(el.textContent) : null;
    } catch (e) {
      return null;
    }
  };

  const cfg = readJson('bg-track-config') || {};
  const ctx = readJson('bg-track-context') || {};
  const configured = Boolean(cfg.enabled && cfg.url && cfg.key);

  /* Einwilligung -------------------------------------------------------------- */
  const browserOptOut = () => navigator.globalPrivacyControl === true || navigator.doNotTrack === '1' || window.doNotTrack === '1';

  const consent = () => {
    try {
      if (browserOptOut()) return false;
      const cp = window.Shopify && window.Shopify.customerPrivacy;
      return Boolean(cp && typeof cp.analyticsProcessingAllowed === 'function' && cp.analyticsProcessingAllowed() === true);
    } catch (e) {
      return false;
    }
  };

  /* Session-Kennung: nur mit Einwilligung, nur sessionStorage --------------------- */
  const newSid = () => {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 22);
  };

  const getSid = () => {
    try {
      let sid = sessionStorage.getItem(SID_KEY);
      if (!sid) {
        sid = newSid();
        sessionStorage.setItem(SID_KEY, sid);
      }
      return sid;
    } catch (e) {
      return null;
    }
  };

  const wipe = () => {
    try {
      sessionStorage.removeItem(SID_KEY);
      sessionStorage.removeItem(STARTED_KEY);
      sessionStorage.removeItem(SURVEY_KEY);
    } catch (e) { /* nichts zu tun */ }
    document.querySelectorAll('.bgt-survey').forEach((el) => el.remove());
  };

  /* Senden --------------------------------------------------------------------- */
  const deviceClass = () => {
    const w = window.innerWidth;
    if (w < 768) return 'mobile';
    if (w < 1100) return 'tablet';
    return 'desktop';
  };

  const source = () => {
    try {
      if (!document.referrer) return 'direct';
      const host = new URL(document.referrer).hostname;
      return host === location.hostname ? 'intern' : host.slice(0, 80);
    } catch (e) {
      return 'direct';
    }
  };

  const send = (type, data) => {
    if (!configured || !TYPES.includes(type)) return;
    if (!consent()) { wipe(); return; }

    const sid = getSid();
    if (!sid) return;

    const body = {
      sid,
      type,
      path: location.pathname.slice(0, 200),
      device: deviceClass(),
      src: source(),
      data: data || {}
    };

    fetch(`${cfg.url.replace(/\/$/, '')}/rest/v1/events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: cfg.key,
        // Neue "sb_publishable_"-Schluessel duerfen nicht als Bearer-Token gesendet werden, nur alte JWT-Schluessel
        ...(String(cfg.key).startsWith('eyJ') ? { Authorization: `Bearer ${cfg.key}` } : {}),
        Prefer: 'return=minimal'
      },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: 'omit'
    }).catch(() => { /* Statistik darf den Shop nie stoeren */ });
  };

  /* Hilfsfunktionen fuer die Daten --------------------------------------------------- */
  // Shopify liefert Suchbegriffe bereits HTML-kodiert (&quot; usw.). Sicher dekodieren: textarea fuehrt nichts aus.
  const decodeEntities = (text) => {
    const box = document.createElement('textarea');
    box.innerHTML = String(text || '');
    return box.value;
  };

  const cleanQuery = (q) => decodeEntities(q)
    .toLowerCase()
    .replace(/\S+@\S+/g, ' ')      // E-Mail-Adressen entfernen
    .replace(/\d{6,}/g, ' ')       // lange Zahlen (Telefon, Kundennummer) entfernen
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80);

  const fitStatus = (tags) => {
    const g = window.BGGarage;
    const vehicle = g && g.get();
    if (!vehicle) return { fit: 'no_vehicle' };
    if (g.isUniversal && g.isUniversal(tags)) return { fit: 'universal', vehicle_id: vehicle.id };
    if (g.fits(tags, vehicle)) return { fit: 'fit', vehicle_id: vehicle.id };
    return { fit: g.vehiclesFor(tags).length ? 'nofit' : 'unlisted', vehicle_id: vehicle.id };
  };

  // Nur ein aktiver Fahrzeug-Filter der Kategorie (z. B. "Fahrzeug: BMW 3er E46 Limousine"), sonst nichts aus der URL
  const vehicleFilter = () => {
    try {
      const params = new URLSearchParams(location.search);
      for (const [key, value] of params) {
        if (key.startsWith('filter.p.tag') && /^Fahrzeug: /.test(value)) return value.slice(0, 80);
      }
    } catch (e) { /* ignorieren */ }
    return '';
  };

  /* Seitenereignisse ---------------------------------------------------------------- */
  let pageDone = false;

  const trackPage = () => {
    if (pageDone) return;
    pageDone = true;

    try {
      if (!sessionStorage.getItem(STARTED_KEY)) {
        sessionStorage.setItem(STARTED_KEY, '1');
        send('session_start', { landing: location.pathname.slice(0, 120) });
      }
    } catch (e) { /* ignorieren */ }

    if (ctx.template === 'product' && ctx.product) {
      const status = fitStatus(ctx.product.tags || []);
      send('product_view', {
        handle: ctx.product.handle,
        price: ctx.product.price,
        available: ctx.product.available,
        ...status
      });
    }

    if (ctx.template === 'search' && ctx.search) {
      const q = cleanQuery(ctx.search.terms);
      if (q) send('search', { q, results: Number(ctx.search.count) || 0 });
    }

    if (ctx.template === 'collection' && ctx.collection) {
      send('listing', { collection: String(ctx.collection.handle || '').slice(0, 80), count: Number(ctx.collection.count) || 0, filter: vehicleFilter() });
    }

    if (ctx.template === 'cart') {
      send('cart_view', { items: ctx.cart ? ctx.cart.count : 0, value: ctx.cart ? ctx.cart.value : 0 });
      scheduleSurvey();
    }
  };

  /* Warenkorb / Kasse ---------------------------------------------------------------- */
  document.addEventListener('on:cart:add', (e) => {
    if (!consent()) return;
    const detail = e.detail || {};
    const cart = detail.cart || {};
    const item = (cart.items || []).find((i) => i.variant_id === detail.variantId) || {};
    send('add_to_cart', {
      handle: item.handle || '',
      price: item.price || 0,
      qty: item.quantity || 1,
      cart_value: cart.total_price || 0
    });
  });

  document.addEventListener('click', (e) => {
    if (!consent()) return;
    const target = e.target.closest('[name="checkout"], a[href^="/checkout"]');
    if (target) send('checkout_click', { value: ctx.cart ? ctx.cart.value : 0 });
  });

  /* Kurzumfrage im Warenkorb ----------------------------------------------------------- */
  const REASONS = [
    ['price', 'Zu teuer'],
    ['compare', 'Ich vergleiche noch'],
    ['later', 'Gerade kein Budget – später'],
    ['fit', 'Unsicher, ob es passt'],
    ['shipping', 'Versand oder Lieferzeit'],
    ['browsing', 'Ich stöbere nur'],
    ['other', 'Etwas anderes']
  ];

  const scheduleSurvey = () => {
    if (!ctx.cart || !ctx.cart.count) return;
    try {
      if (sessionStorage.getItem(SURVEY_KEY)) return;
    } catch (e) { return; }
    setTimeout(showSurvey, 7000);
  };

  const showSurvey = () => {
    if (!consent() || document.querySelector('.bgt-survey')) return;
    try {
      if (sessionStorage.getItem(SURVEY_KEY)) return;
    } catch (e) { return; }

    const host = document.querySelector('main') || document.body;
    const box = document.createElement('section');
    box.className = 'bgt-survey';
    box.setAttribute('aria-live', 'polite');
    box.innerHTML = `
      <p class="bgt-survey__title">Kurze Frage: Was hält dich noch ab?</p>
      <p class="bgt-survey__sub">Eine Antwort genügt, sie ist anonym und hilft uns, unser Angebot zu verbessern.</p>
      <div class="bgt-survey__opts"></div>
      <button type="button" class="bgt-survey__skip">Nicht jetzt</button>
    `;

    const opts = box.querySelector('.bgt-survey__opts');
    REASONS.forEach(([value, label]) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'bgt-survey__btn';
      button.textContent = label;
      button.addEventListener('click', () => {
        send('survey', { reason: value, value: ctx.cart ? ctx.cart.value : 0, items: ctx.cart ? ctx.cart.count : 0 });
        try { sessionStorage.setItem(SURVEY_KEY, 'done'); } catch (e) { /* ignorieren */ }
        box.innerHTML = '<p class="bgt-survey__title">Danke für deine Antwort!</p>';
        setTimeout(() => box.remove(), 2500);
      });
      opts.append(button);
    });

    box.querySelector('.bgt-survey__skip').addEventListener('click', () => {
      try { sessionStorage.setItem(SURVEY_KEY, 'skipped'); } catch (e) { /* ignorieren */ }
      box.remove();
    });

    host.append(box);
  };

  /* Start: erst wenn und solange zugestimmt ist ------------------------------------------- */
  const evaluate = () => {
    if (!configured) return;
    if (consent()) trackPage();
    else { pageDone = false; wipe(); }
  };

  window.BGTrack = {
    event: (type, data) => send(type, data),
    active: () => configured && consent()
  };

  if (configured) {
    evaluate();
    // Shopify meldet jede Aenderung der Einwilligung; zusaetzlich kurz warten, falls die API spaet geladen wird
    document.addEventListener('visitorConsentCollected', evaluate);
    let tries = 0;
    const poll = setInterval(() => {
      tries += 1;
      evaluate();
      if (pageDone || tries > 20) clearInterval(poll);
    }, 1500);
  }
})();
